import React from 'react';
import { Await } from '../../../components/Await';
import { imageUrl, useLpRedirect, useSanity } from '../../../lib/landingPage';
import { observer } from 'mobx-react-lite';
import { Redirect, generatePath, useRouteMatch } from 'react-router-dom';
import { Header } from '../Header';
import { BlogSubmenu } from '../BlogSubmenu';
import { categoriesQuery } from '../BlogPage/BlogHomePage';
import { Footer } from '../Footer';
import { SanityObject } from '../../../lib/landingPage';
import { ImageProps } from '../Image';
import { BackButton } from '../../../components/BackButton/BackButton';
import { ActiveCampaignForm } from '../../../components/landingPage/ActiveCampaignForm';
import { ContentFitIframe } from '../../../components/landingPage/ContentFitIframe';
import routes from '../../../pages/landingPage/routes';
import Styles from './AludeNews.module.less';
import { classes } from '../../../lib';
import { AludeNewsPostProps } from './AludeNewsPage';
import { prepareHtml } from './formatters';
import { AludeNewsPostPreview } from '../../../components/landingPage/AludeNewsPostPreview';
import { ShareButtons } from '../../../components/landingPage/ShareButtons';
import { LP_BASE_URL } from '../../../config';
import { NewsletterForm } from '../../../components/landingPage/NewsletterForm';
import { BlogPostCategory } from '../../../components/landingPage/BlogPost';

type SanityAludeNewsPost = {
    subject: string;
    html: string;
    slug: string;
    imageRef: SanityObject<ImageProps>;
    _createdAt: string;
};

const MorePosts = ({
    currentPostSlug,
    aludeNewsPosts,
}: {
    currentPostSlug: string;
    aludeNewsPosts: SanityObject<AludeNewsPostProps>[];
}) => {
    return (
        <div className="vertical g24">
            <h3>Leia também...</h3>
            <div>
                <div className={classes(Styles.postsGrid)}>
                    {aludeNewsPosts
                        .filter(p => p.slug !== currentPostSlug)
                        .slice(0, 3)
                        .map(p => (
                            <AludeNewsPostPreview
                                key={p.slug}
                                subject={p.subject}
                                image={imageUrl(p.imageRef.image, 'blog').url()}
                                slug={p.slug}
                            />
                        ))}
                </div>
            </div>
        </div>
    );
};

const Sidebar = () => {
    return (
        <div className={Styles.aludeNewsSidebar}>
            <h4>Sua imobiliária mais inteligente em 5 minutos</h4>
            <p>
                Inscreva-se gratuitamente e receba as próximas AludeNews na
                caixa de entrada do seu e-mail por volta das 6h toda segunda,
                quarta e sexta.
            </p>
            <p>Preencha o formulário abaixo agora mesmo:</p>
            <ActiveCampaignForm activeCampaignId={7} />
        </div>
    );
};

type AludeNewsPostPageResolvedProps = {
    aludeNewsPost: SanityAludeNewsPost;
    posts: SanityObject<AludeNewsPostProps>[];
    categories: BlogPostCategory[];
};

const Content = ({ post }: { post: SanityAludeNewsPost }) => {
    const MAX_WIDTH = 600;
    const PADDING = 16;
    const onResize = (width: number) => {
        const html = `${prepareHtml(post.html, width < MAX_WIDTH)}`;
        return html.replaceAll(
            '"580"',
            `"${(width > MAX_WIDTH ? MAX_WIDTH : width) - PADDING * 2}"`
        );
    };
    return (
        <ContentFitIframe
            html={prepareHtml(post.html, false)}
            maxWidth={MAX_WIDTH}
            onResize={onResize}
        />
    );
};

const AludeNewsPostPageResolved = observer(
    ({ aludeNewsPost, posts, categories }: AludeNewsPostPageResolvedProps) => {
        const redirect = useLpRedirect();
        return (
            <>
                <Header showSignupButton />
                <BlogSubmenu categories={categories} />
                <div className={Styles.container}>
                    <div className={Styles.post}>
                        <div className="vertical g16 mt-hidden">
                            <ShareButtons
                                url={`${LP_BASE_URL}${generatePath(
                                    routes.blog.news.post.path,
                                    {
                                        postSlug: aludeNewsPost.slug,
                                    }
                                )}`}
                                text={aludeNewsPost.subject}
                            />
                        </div>
                        <div className="vertical g32">
                            <BackButton
                                onClick={() => redirect(routes.blog.news.path)}>
                                Voltar para a home do AludeNews
                            </BackButton>
                            <Content post={aludeNewsPost} />
                        </div>
                        <Sidebar />
                    </div>
                    <div className="vertical g48">
                        <div className="vertical g32">
                            <h4>Compartilhe este post</h4>
                            <div className="horizontal g16">
                                <ShareButtons
                                    url={`${LP_BASE_URL}${generatePath(
                                        routes.blog.news.post.path,
                                        {
                                            postSlug: aludeNewsPost.slug,
                                        }
                                    )}`}
                                    text={aludeNewsPost.subject}
                                />
                            </div>
                        </div>
                        <MorePosts
                            currentPostSlug={aludeNewsPost.slug}
                            aludeNewsPosts={posts}
                        />
                        <NewsletterForm />
                    </div>
                </div>
                <Footer hideMenu={false} />
            </>
        );
    }
);

export const AludeNewsPostPage = observer(() => {
    const sanity = useSanity();
    const match = useRouteMatch<{ postSlug: string }>({
        path: routes.blog.news.post.path,
    });
    const postSlug = match?.params.postSlug;
    if (!postSlug) return <Redirect to={'/'} />;
    return (
        <Await
            views={{
                aludeNewsPost: sanity.asyncAludeNewsPost(postSlug),
                posts: sanity.asyncAludeNewsPosts(0),
                categories: sanity.asyncBlog(categoriesQuery),
            }}
            resolved={AludeNewsPostPageResolved}
        />
    );
});
